import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { useIsSkeleton } from '@/components/Skeleton'
import { QuestionIcon } from '@/features/legends/questions/components/QuestionIcon'
import type { QuestionType } from '@/features/legends/questions/question-types'
import styles from './Toolbox.module.css'

/**
 * An element type the toolbox offers.
 */
export interface ToolboxItem {
  type: QuestionType
  title: string
}

/**
 * A titled group of the toolbox's elements.
 */
export interface ToolboxGroup {
  title: string
  items: readonly ToolboxItem[]
}

export interface ToolboxProps {
  groups: readonly ToolboxGroup[]
  isDisabled?: boolean
  /**
   * Whether we're rendering before the legend has loaded. The elements show
   * but can't be added.
   */
  isSkeleton?: boolean
  onAdd: (type: QuestionType) => unknown
  onDragStart: (type: QuestionType) => unknown
  onDragEnd: () => unknown
  testId?: string
}

/**
 * The elements that can be added, dragged onto the canvas or pressed.
 */
export function Toolbox({
  groups,
  isDisabled,
  isSkeleton,
  onAdd,
  onDragStart,
  onDragEnd,
  testId,
}: ToolboxProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  isDisabled ||= isSkeleton
  return (
    <aside className={styles.root} data-test={testId}>
      {groups.map((group) => (
        <section key={group.title} className={styles.group}>
          <h4 className={styles.groupTitle}>{group.title}</h4>
          <div className={styles.items}>
            {group.items.map((item) => (
              <RawButton
                key={item.type}
                className={styles.item}
                isDisabled={isDisabled}
                draggable={!isDisabled}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'copy'
                  // Firefox only starts a drag with data.
                  e.dataTransfer.setData('text/plain', '')
                  onDragStart(item.type)
                }}
                onDragEnd={onDragEnd}
                onPress={() => onAdd(item.type)}
                testId={testId && `${testId}-${item.type}`}
              >
                <QuestionIcon type={item.type} size={22} />
                <span className={styles.itemTitle}>{item.title}</span>
                <Icon
                  name="add"
                  size={14}
                  className={styles.itemAdd}
                  aria-hidden="true"
                />
              </RawButton>
            ))}
          </div>
        </section>
      ))}
      <p className={styles.hint}>
        Drag elements onto the canvas, or click one to add it after the selected
        element.
      </p>
    </aside>
  )
}
